<template>
  <LoadingComponent v-if="loading" />
  <template v-else-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <BaseButton
            v-if="$can('manage_invoicing')"
            :title="$t('button.add_order')"
            color="warning"
            icon="plus"
            size="small"
            @click="addNewSupplierOrder"
          />
          <DashboardPanelControl
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :need-save-locked="loading"
            :count-filter="validFilter.length ?? 0"
            @dashboard-changed="dashboardChanged"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <AlertPanel
        v-if="limitAlert"
        type="warning"
        compact
        :title="$t(`alert.${limitAlert.code}`, { limit: limitAlert.limit })"
        :actions="[
          {
            id: 'force-unlimited',
            name: $t('button.display_all_results'),
            action: overrideLimit
          }
        ]"
      />
      <SupplierOrdersContainer
        :supplier-orders="supplier_orders"
        :metadata="metadata"
        :pagination="pagination"
        @fetch-supplier-orders="fetchSupplierOrders"
        @page-change="handlePageChange"
      />
    </div>
  </template>
  <SupplierOrderModal
    v-model:open="isSupplierOrderModalOpen"
    :supplier-order-id="null"
  />
</template>

<script>
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import { mapActions, mapGetters } from 'vuex'
import SupplierOrdersContainer from '@c/Dashboard/Content/SupplierOrder/components/SupplierOrdersContainer.vue'
import SupplierOrderModal from '@ui/modal/supplierOrder/SupplierOrderModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import AlertPanel from '@ui/AlertPanel.vue'

export default {
  name: 'SupplierOrderContent',
  components: {
    AlertPanel,
    BaseButton,
    SupplierOrderModal,
    SupplierOrdersContainer,
    LoadingComponent,
    DashboardPanelControl,
    FilterLinesComponent,
    DashboardHeader
  },
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localDashboard: null,
      loading: false,
      isSupplierOrderModalOpen: false,
      supplier_orders: [],
      metadata: [],
      supplierOrder: null,
      requireSaveDashboard: false,
      page: 1,
      perPage: 50,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 50
      },
      limitAlert: null,
      forceUnlimited: false
    }
  },
  computed: {
    ...mapGetters({
      availableFilter: 'metadata/getAvailableFilterSupplierOrder'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loading = true
        this.localDashboard = newValue
        this.fetchSupplierOrderDashboardConfig()
      }
    },
    isSupplierOrderModalOpen(newValue, oldValue) {
      if (newValue !== oldValue && newValue === false) {
        this.page = 1
        this.fetchSupplierOrders()
      }
    }
  },
  mounted() {
    this.fetchSupplierOrderDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processFetchAllSupplierOrdersAction:
        'supplierOrder/fetchAllSupplierOrders',
      processFetchNewSupplierOrderAction: 'invoicing/fetchNewSupplierOrder'
    }),
    addNewSupplierOrder() {
      this.isSupplierOrderModalOpen = true
    },
    fetchSupplierOrders: function () {
      this.limitAlert = null
      let params = {
        filters: this.validFilter,
        page: this.page,
        per_page: this.perPage,
        force_unlimited: this.forceUnlimited
      }
      this.processFetchAllSupplierOrdersAction(params).then((response) => {
        this.supplier_orders = response.supplier_orders
        this.metadata = response.metadata
        this.pagination = response.pagination ?? this.pagination
        this.limitAlert = response.alert ?? null
      })
    },
    handlePageChange(page) {
      this.page = page
      this.fetchSupplierOrders()
    },
    overrideLimit() {
      this.forceUnlimited = true
      this.page = 1
      this.fetchSupplierOrders()
    },
    fetchSupplierOrderDashboardConfig() {
      this.loading = true
      this.page = 1
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.fetchSupplierOrders()
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally((_) => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.page = 1
      this.fetchSupplierOrders()
    },
    dashboardChanged(_dashboard_template_id) {
      this.fetchSupplierOrderDashboardConfig()
    }
  }
}
</script>
